import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, MessageSquare, ThumbsDown, ThumbsUp } from "lucide-react";
import { api } from "@/lib/api";
import { ObservarRLModal } from "./ObservarRLModal";
import { RechazarRLModal } from "./RechazarRLModal";

export interface ObservacionQa {
  campo?: string | null;
  documentoId?: string | null;
  observacion: string;
  ts?: string | null;
  autorId?: string | null;
}

/**
 * Sección embebida en el detalle del RL que aparece cuando estatus es
 * EN_REVISION_QA u OBSERVADO. Muestra meta de revisión + observaciones
 * acumuladas + 3 acciones de Calidad (aprobar / observar / rechazar).
 *
 * Sólo roles GERENTE_CALIDAD / AUXILIAR_CALIDAD / ADMIN_SICEN tendrán los
 * botones activos en el backend; aquí también validamos para no mostrar
 * acciones si el caller no tiene rol.
 */
export function RevisionCalidadSection({
  rlId,
  estatus,
  pasoActual,
  fechaEnvioRevision,
  solicitanteCorreo,
  motivoRechazo,
  observaciones,
  esCalidad,
}: {
  rlId: string;
  estatus: string;
  pasoActual: number;
  fechaEnvioRevision: string | null;
  solicitanteCorreo: string | null;
  motivoRechazo: string | null;
  observaciones: ObservacionQa[] | null;
  esCalidad: boolean;
}) {
  const qc = useQueryClient();
  const [observarOpen, setObservarOpen] = useState(false);
  const [rechazarOpen, setRechazarOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [folioAprobado, setFolioAprobado] = useState<string | null>(null);

  const enRevision = estatus === "EN_REVISION_QA";
  const observado = estatus === "OBSERVADO";
  const rechazado = estatus === "RECHAZADO";

  const aprobar = useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ folio: string }>(`/api/registros-legales/${rlId}/aprobar`);
      return data.folio;
    },
    onSuccess: (folio) => {
      setFolioAprobado(folio);
      qc.invalidateQueries({ queryKey: ["registro-legal", rlId] });
      qc.invalidateQueries({ queryKey: ["revision-rl"] });
    },
    onError: (e: unknown) => {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    },
  });

  const onAccionDone = () => {
    qc.invalidateQueries({ queryKey: ["registro-legal", rlId] });
    qc.invalidateQueries({ queryKey: ["revision-rl"] });
  };

  const tono = enRevision
    ? "border-estado-info/25 bg-estado-info-bg"
    : observado
      ? "border-estado-warn/25 bg-estado-warn-bg"
      : rechazado
        ? "border-estado-err/25 bg-estado-err-bg"
        : "border-edge bg-surface";

  return (
    <section className={`rounded-lg border shadow-sm ${tono}`}>
      <div className="border-b border-inherit px-4 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
          Revisión Calidad
        </h2>
      </div>
      <div className="space-y-3 p-4">
        <div className="grid gap-2 sm:grid-cols-3">
          <Meta label="Estatus" valor={labelEstatus(estatus)} />
          <Meta label="Solicitante" valor={solicitanteCorreo ?? "—"} />
          <Meta label="Paso del wizard" valor={`${pasoActual} de 4`} />
          <Meta label="Enviado a revisión" valor={fechaCorta(fechaEnvioRevision)} />
        </div>

        {rechazado && motivoRechazo && (
          <div className="rounded border border-estado-err/25 bg-surface p-3 text-sm">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-estado-err">Motivo de rechazo</div>
            <div className="whitespace-pre-wrap text-fg">{motivoRechazo}</div>
          </div>
        )}

        {observaciones && observaciones.length > 0 && (
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-fg-muted">
              Observaciones acumuladas ({observaciones.length})
            </div>
            <ul className="space-y-2">
              {observaciones.map((o, i) => (
                <li key={i} className="rounded border bg-surface p-3 text-sm">
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-fg-subtle">
                    {o.campo && (
                      <span className="rounded bg-surface-2 px-2 py-0.5 font-mono">{o.campo}</span>
                    )}
                    {o.documentoId && (
                      <span className="rounded bg-surface-2 px-2 py-0.5 font-mono text-[10px]">
                        doc {o.documentoId.substring(0, 8)}…
                      </span>
                    )}
                    {!o.campo && !o.documentoId && (
                      <span className="rounded bg-surface-2 px-2 py-0.5 text-[10px] uppercase">Global</span>
                    )}
                    <span>· {fechaCorta(o.ts ?? null)}</span>
                  </div>
                  <div className="whitespace-pre-wrap text-fg">{o.observacion}</div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {folioAprobado && (
          <div className="flex items-start gap-2 rounded border border-estado-ok/25 bg-estado-ok-bg p-3 text-sm text-estado-ok">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              RL aprobado. Folio asignado: <strong className="font-mono">{folioAprobado}</strong>.
            </span>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded border bg-estado-warn-bg p-3 text-sm text-estado-warn">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {esCalidad && enRevision && (
          <div className="flex flex-wrap gap-2 border-t pt-3">
            <button
              type="button"
              onClick={() => {
                setError(null);
                aprobar.mutate();
              }}
              disabled={aprobar.isPending}
              className="inline-flex items-center gap-1 rounded bg-action-ok px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            >
              <ThumbsUp className="h-4 w-4" />
              {aprobar.isPending ? "Aprobando…" : "Aprobar"}
            </button>
            <button
              type="button"
              onClick={() => setObservarOpen(true)}
              className="inline-flex items-center gap-1 rounded bg-action-warn px-3 py-1.5 text-sm font-medium text-white transition hover:bg-action-warn-hover"
            >
              <MessageSquare className="h-4 w-4" />
              Observar
            </button>
            <button
              type="button"
              onClick={() => setRechazarOpen(true)}
              className="inline-flex items-center gap-1 rounded bg-action-err px-3 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              <ThumbsDown className="h-4 w-4" />
              Rechazar
            </button>
          </div>
        )}

        {observado && (
          <div className="rounded border bg-surface p-3 text-sm text-fg-muted">
            Esperando subsanación del cliente. Las observaciones ya están notificadas.
          </div>
        )}
      </div>

      <ObservarRLModal
        open={observarOpen}
        rlId={rlId}
        onClose={() => setObservarOpen(false)}
        onDone={onAccionDone}
      />
      <RechazarRLModal
        open={rechazarOpen}
        rlId={rlId}
        onClose={() => setRechazarOpen(false)}
        onDone={onAccionDone}
      />
    </section>
  );
}

function Meta({ label, valor }: { label: string; valor: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      <div className="text-sm text-fg">{valor}</div>
    </div>
  );
}

function fechaCorta(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function labelEstatus(e: string): string {
  return (
    {
      EN_REVISION_QA: "En revisión por Calidad",
      OBSERVADO: "Con observaciones por subsanar",
      RECHAZADO: "Rechazado",
      APROBADO: "Aprobado",
      ACTIVO: "Activo",
    }[e] ?? e
  );
}
